import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BulkResumeUploadService, DirectoryUploadItem, BulkUploadSummary } from 'src/app/core/services/bulk-resume-upload.service';
import { AlertAndNotificationsService } from 'src/app/core/services/alert-and-notifications.service';
import { ResumeService } from 'src/app/core/services/resume.service';
import { AuthService } from 'src/app/core/services/auth.service';

declare var $: any;

const DEFAULT_DIRECTORY = 'Default Directory';
const VALID_FILE_EXTENSIONS = ['.pdf', '.xlsx', '.docx', '.doc'];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

@Component({
  selector: 'app-bulk-resume-upload',
  templateUrl: './bulk-resume-upload.component.html',
  styleUrls: ['./bulk-resume-upload.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkResumeUploadComponent implements OnInit, OnDestroy {
  @Input() jobId: string;
  @Input() sourceTypeList: any[] = [];
  @Input() disableSourceTypesFlag: boolean = false;
  @Input() initialSourceTypeId: string;
  @Input() initialUploadDate: Date;
  @Output() completeCallback = new EventEmitter<BulkUploadSummary>();
  @Output() selectionCleared = new EventEmitter<void>();

  // Component state
  selectedDirectories: DirectoryUploadItem[] = [];
  skippedFiles: { name: string; reason: string }[] = [];
  duplicateFiles: { name: string }[] = [];
  sourceType: any;
  sourceTypeId: string;
  otherSourceTypeNotes: string;
  referrerName: string;
  referrerEmail: string;
  uploadDate: string;
  displayUploadDate: string;
  userId: string;
  companyId: string;

  // UI flags
  disableUploadButtonFlag: boolean = true;
  fileUploadInProgressFlag: boolean = false;

  private subscriptions = new Subscription();

  constructor(
    private bulkUploadService: BulkResumeUploadService,
    private alertsAndNotificationsService: AlertAndNotificationsService,
    private bsModalService: BsModalService,
    private resumeService: ResumeService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) { }

  ngOnInit() {
    this.userId = this.authService.getUserDetails().id;
    this.companyId = this.authService.getUserDetails().company.companyId;
    this.initializeComponent();
    this.subscribeToUploadComplete();
    this.subscribeToSummaryClosed();
    this.loadSourceTypes();
    this.initializeDatePicker();
  }


  /**
   * Initialize component variables and setup
   */
  initializeComponent(): void {
    const uploadDate = this.initialUploadDate || new Date();
    this.displayUploadDate = this.formatDateForDisplay(uploadDate);
    this.uploadDate = this.formatDateForAPI(uploadDate);
  }

  /**
   * Subscribe to upload complete event
   */
  subscribeToUploadComplete(): void {
    this.subscriptions.add(this.bulkUploadService.uploadComplete$.subscribe(() => {
      this.fileUploadInProgressFlag = false;
      this.cdr.markForCheck();
    }));
  }

  subscribeToSummaryClosed(): void {
    this.subscriptions.add(this.bulkUploadService.summaryClosed$.subscribe(summary => this.completeAfterModalHidden(summary)));
  }

  ngOnDestroy(): void {
    $('#displayUploadDate').datepicker('destroy');
    this.subscriptions.unsubscribe();
  }

  onPicked(input: HTMLInputElement): void {
    const files = Array.from(input.files || []);
    input.value = '';
    this.onDirectoriesSelected({ target: { files } });
  }

  onDirectoriesSelected(event: any): void {
    const files = Array.from(event.target.files) as File[];

    if (files.length === 0) {
      this.alertsAndNotificationsService.showBannerMessage(
        'No files selected. Please select at least one directory with resume files.',
        'warning'
      );
      return;
    }

    const knownKeys = new Set<string>();
    this.selectedDirectories.forEach(dir => dir.files.forEach(file => knownKeys.add(this.fileKey(file))));
    const directoriesMap = new Map<string, File[]>();

    files.forEach(file => {
      const skipReason = this.skipReason(file);
      if (skipReason) {
        this.skippedFiles.push({ name: file.name, reason: skipReason });
        return;
      }
      const key = this.fileKey(file);
      if (knownKeys.has(key)) {
        this.duplicateFiles.push({ name: file.name });
        return;
      }
      knownKeys.add(key);

      const webkitPath = (file as any).webkitRelativePath;
      const parts = webkitPath ? webkitPath.split('/') : [file.name];
      const dirName = parts.length > 1 ? parts[0] : DEFAULT_DIRECTORY;
      if (!directoriesMap.has(dirName)) {
        directoriesMap.set(dirName, []);
      }
      directoriesMap.get(dirName).push(file);
    });

    directoriesMap.forEach((dirFiles, dirName) => {
      const existing = this.selectedDirectories.find(dir => dir.directoryName === dirName);
      if (existing) {
        existing.files = [...existing.files, ...dirFiles];
      } else {
        this.selectedDirectories = [...this.selectedDirectories, {
          directoryName: dirName,
          files: dirFiles,
          uploadDate: this.uploadDate,
          displayDate: this.displayUploadDate
        }];
      }
    });

    this.updateUploadButtonState();
    this.cdr.markForCheck();
  }

  removeDirectory(index: number): void {
    this.selectedDirectories.splice(index, 1);
    this.afterSelectionChanged();
  }

  removeFile(directory: DirectoryUploadItem, file: File): void {
    directory.files = directory.files.filter(f => f !== file);
    if (directory.files.length === 0) {
      this.selectedDirectories = this.selectedDirectories.filter(dir => dir !== directory);
    }
    this.afterSelectionChanged();
  }

  removeDuplicate(index: number): void {
    this.duplicateFiles.splice(index, 1);
    this.afterSelectionChanged();
  }

  removeSkipped(index: number): void {
    this.skippedFiles.splice(index, 1);
    this.afterSelectionChanged();
  }

  clearAllDirectories(): void {
    this.selectedDirectories = [];
    this.duplicateFiles = [];
    this.skippedFiles = [];
    this.afterSelectionChanged();
  }

  private afterSelectionChanged(): void {
    this.updateUploadButtonState();
    this.cdr.markForCheck();
    if (this.selectedDirectories.length === 0 && this.skippedFiles.length === 0 && this.duplicateFiles.length === 0) {
      this.selectionCleared.emit();
    }
  }

  private skipReason(file: File): string {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!VALID_FILE_EXTENSIONS.includes(ext)) { return 'Unsupported type, skipped'; }
    return file.size > MAX_FILE_SIZE ? 'Over 10 MB, skipped' : '';
  }

  private fileKey(file: File): string {
    return ((file as any).webkitRelativePath || file.name) + '|' + file.size;
  }

  /**
   * Update upload button state based on current conditions
   */
  updateUploadButtonState(): void {
    // Button should be enabled as soon as at least one directory is selected.
    // Keep it disabled while an upload is in progress.
    this.disableUploadButtonFlag = this.selectedDirectories.length === 0 || this.fileUploadInProgressFlag;
  }

  /**
   * Source type changed handler
   */
  sourceTypeChanged(): void {
    if (this.sourceType && this.sourceType.id) {
      this.sourceTypeId = this.sourceType.id;
      this.updateUploadButtonState();
    } else {
      this.sourceTypeId = "";
      this.updateUploadButtonState();
    }
    this.cdr.markForCheck();
  }

  /**
   * Load source types from the service
   */
  loadSourceTypes(): void {
    this.resumeService.getAllSourceTypes(
      (data) => {
        this.sourceTypeList = data || [];
        const match = this.sourceTypeList.find(source => source.id === this.initialSourceTypeId);
        if (match) {
          this.sourceType = match;
          this.sourceTypeChanged();
        }
        this.cdr.markForCheck();
      },
      (error) => {
        this.alertsAndNotificationsService.showBannerMessage(
          'Failed to load source types. Please refresh and try again.',
          'danger'
        );
      }
    );
  }

  /**
   * Initialize date picker
   */
  initializeDatePicker(): void {
    // Wait for the form to render before attaching the picker to its input.
    setTimeout(() => {
      const dateInput = $('#displayUploadDate');
      dateInput.datepicker({
        format: 'M d, yyyy',
        endDate: new Date(),
        weekStart: 1,
        autoclose: true
      });
      dateInput.datepicker('setDate', this.initialUploadDate || new Date());
      dateInput.on('changeDate', (e: any) => {
        if (e.date) {
          this.displayUploadDate = this.formatDateForDisplay(e.date);
          this.uploadDate = this.formatDateForAPI(e.date);
          this.onUploadDateChange();
        }
      });
    }, 100);
  }

  openDatePicker(): void {
    if (this.fileUploadInProgressFlag) { return; }
    $('#displayUploadDate').datepicker('show');
  }

  /**
   * Handle upload date change
   */
  onUploadDateChange(): void {
    // Update upload date for all directories
    this.selectedDirectories.forEach(dir => {
      dir.uploadDate = this.uploadDate;
      dir.displayDate = this.displayUploadDate;
    });
    this.cdr.markForCheck();
  }

  /**
   * Start the bulk upload process
   */
  startBulkUpload(): void {
    if (!this.validateUploadConfiguration()) {
      return;
    }

    this.fileUploadInProgressFlag = true;
    this.disableUploadButtonFlag = true;
    this.bulkUploadService.resetProgress();

    // Prepare source type details
    const sourceTypeDetails = {
      otherSourceTypeNotes: this.otherSourceTypeNotes,
      referrerName: this.referrerName,
      referrerEmail: this.referrerEmail,
      uploadDate: this.uploadDate
    };

    // Start the bulk upload (asynchronous)
    this.bulkUploadService.processBulkUpload(
      this.selectedDirectories,
      this.sourceTypeId,
      this.userId,
      this.companyId,
      '', // folderIds – populate from context when available
      sourceTypeDetails,
      this.jobId
    ).catch(error => {
      console.error('Bulk upload failed:', error);
      this.alertsAndNotificationsService.showAlertWithCallBack(
        'Upload Error',
        `An error occurred during bulk upload: ${error.message}`,
        'danger',
        () => { }
      );
      this.fileUploadInProgressFlag = false;
      this.updateUploadButtonState();
    });

    this.cdr.markForCheck();
  }

  /**
   * Validate upload configuration
   */
  validateUploadConfiguration(): boolean {
    if (this.selectedDirectories.length === 0) {
      this.alertsAndNotificationsService.showAlertWithCallBack(
        'No Files Selected',
        'Please select at least one directory with resume files.',
        'warning',
        () => { }
      );
      return false;
    }

    if (!this.sourceTypeId) {
      this.alertsAndNotificationsService.showAlertWithCallBack(
        'Source Type Required',
        'Please select a source type before uploading.',
        'warning',
        () => { }
      );
      return false;
    }

    if (this.sourceType && this.sourceType.sourceType === 'Other' && !this.otherSourceTypeNotes) {
      this.alertsAndNotificationsService.showAlertWithCallBack(
        'Notes Required',
        'Please enter notes for the "Other" source type.',
        'warning',
        () => { }
      );
      return false;
    }

    if (this.sourceType && this.sourceType.sourceType === 'Referral') {
      if (!this.referrerName || !this.referrerEmail) {
        this.alertsAndNotificationsService.showAlertWithCallBack(
          'Referrer Details Required',
          'Please enter both referrer name and email.',
          'warning',
          () => { }
        );
        return false;
      }
    }

    return true;
  }

  // Navigating while the modal is still hiding leaves ngx-bootstrap's body state (modal-open,
  // overflow, padding) behind, which hides the page scrollbar on the next screen.
  private completeAfterModalHidden(summary: BulkUploadSummary): void {
    this.bsModalService.onHidden.pipe(take(1)).subscribe(() => {
      setTimeout(() => {
        if ($('.modal.in, .modal.show').length === 0) {
          $('body').removeClass('modal-open').css({ overflow: '', 'padding-right': '' });
        }
        this.completeCallback.emit(summary);
      });
    });
  }

  /**
   * Helper: Format date for display (M d, yyyy)
   */
  formatDateForDisplay(date: Date): string {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  }

  /**
   * Helper: Format date for API (ISO format)
   */
  formatDateForAPI(date: Date): string {
    return date.toISOString();
  }

  /**
   * Get total files count
   */
  totalFilesCount(): number {
    return this.selectedDirectories.reduce((total, dir) => total + dir.files.length, 0);
  }

  folderCount(): number {
    return this.selectedDirectories.filter(dir => !this.isDefaultDirectory(dir)).length;
  }

  looseFileCount(): number {
    return this.selectedDirectories.filter(dir => this.isDefaultDirectory(dir))
      .reduce((total, dir) => total + dir.files.length, 0);
  }

  isDefaultDirectory(directory: DirectoryUploadItem): boolean {
    return directory.directoryName === DEFAULT_DIRECTORY;
  }

  directorySize(directory: DirectoryUploadItem): number {
    return directory.files.reduce((total, file) => total + file.size, 0);
  }

  formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) { return (bytes / 1024 / 1024).toFixed(1) + ' MB'; }
    return Math.max(1, Math.round(bytes / 1024)) + ' KB';
  }
}
